import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  model,
  ModelSignal,
  output,
  signal,
  TemplateRef,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { DateFormat, Pattern } from '@fcom/core/utils/date.format';
import { isPresent } from '@fcom/core/utils/utils';
import { LocalDate, TzDate } from '@fcom/core/utils/tz-date';
import { HistogramBar } from '@fcom/common/interfaces/histogram.interface';
import { MediaQueryService } from '@fcom/common/services/media-query/media-query.service';
import { UiTranslateModule } from '@fcom/ui-translate/ui-translate.module';

import { DatepickerTitleAreaOptions } from '../../interfaces';
import { DateSelection, Day, Month } from '../../../utils/date.interface';
import { ParseUserInput } from '../../../utils/date.format.utils';
import { NotificationLayout, NotificationScale, NotificationTheme } from '../../notifications/enums';
import { CalendarNavigationType, DateRange } from '../interfaces';
import { ButtonTheme } from '../../buttons/button/enums';
import { IconPosition } from '../../icons/enums';
import { IconComponent } from '../../icons/icon/icon.component';
import { ButtonComponent } from '../../buttons/button/button.component';
import { CalendarComponent } from '../calendar/calendar.component';
import { NotificationComponent } from '../../notifications/notification/notification.component';

const DEFAULT_TITLE_AREA_CONFIGURATION: DatepickerTitleAreaOptions = {
  titleAreaStyle: 'ps-large',
  title: undefined,
  titleStyle: 'font-body-medium',
};

const DAYS_AMOUNT = 360; // Default calendar size

export interface UiLabels {
  selectedDates?: string;
  selectedDate?: string;
  title?: string;
  reset?: string;
  selectStartDate?: string;
  selectEndDate?: string;
  inputPlaceholder?: string;
  close?: string;
  done?: string;
  error: {
    invalidDate: string;
    outOfRange: string;
    startAfterEnd: string;
    endBeforeStart: string;
  };
  selected?: string;
  showPreviousMonth?: string;
  showNextMonth?: string;
}

export type DateLabels = Record<string, string> & { dateFormatUserInput: string };

@Component({
  selector: 'fcom-datepicker',
  styleUrls: ['./datepicker.component.scss'],
  templateUrl: './datepicker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NgTemplateOutlet,
    IconComponent,
    FormsModule,
    ButtonComponent,
    CalendarComponent,
    NotificationComponent,
    UiTranslateModule,
  ],
})
export class DatepickerComponent implements AfterViewInit {
  private mediaQueryService = inject(MediaQueryService);
  private hostElement = inject(ElementRef);

  readonly IconLibrary = IconLibrary;
  readonly NotificationScale = NotificationScale;

  titleElement = viewChild<ElementRef<HTMLElement>>('titleElement');

  doneBtnHidden = input(false);
  showCloseButton = input(true);

  /**
   * Object of translated and localized date labels
   * (e.g. month names, date formats)
   */
  dateLabels = input<DateLabels>();

  /**
   * Object of translated UI labels
   * (e.g. error messages, instructions)
   */
  uiLabels = input<UiLabels>();

  titleAreaConfiguration = input<DatepickerTitleAreaOptions>();

  /**
   * H2 title to be shown in top left of modal
   */
  title = input<string>(undefined);
  subTitle = input<TemplateRef<ElementRef>>();

  /**
   * Initially preselected dates
   * Array of (one or two) YYYY-MM-DD strings
   */
  selectedDates = input<[LocalDate] | DateRange>();

  /**
   * Defined range of calendar (first and last selectable date)
   * Array of two YYYY-MM-DD strings
   */
  selectableDatesRange = input<DateRange>();

  /**
   * Ranges where dates should be disabled
   * An array which includes array of two LocalDates
   */
  disabledDateRanges = input<DateRange[]>();

  /**
   * If datepicker should select range or single date
   */
  isDateRange = input(false);

  /**
   * Amount of months to be visible in the calendar (passed through to fcom-calendar)
   */
  displayMonths = input<1 | 2>(2);

  /**
   * Renders the datepicker embedded in a page rather than inside a modal
   */
  embedded = input(false);

  /**
   * Focus header on init
   */
  focusHeaderOnInit = input(false);

  topNotification = input<string>(undefined);
  topNotificationIcon = input<SvgLibraryIcon>(SvgLibraryIcon.INFO);

  /**
   * Show month chip buttons on top of calendar
   */
  showTags = input(true);

  /**
   * Show histogram on top of the calendar
   */
  headerTemplate = input<TemplateRef<HistogramBar[]>>();

  /**
   * Custom day template to be rendered within the calendar
   */
  dayTemplate = input<TemplateRef<{ dayValue: Day; dayString: number }>>();

  /**
   * Set the month index where the calendar should scroll to
   */
  scrollToMonthIndex = input<number>();

  showAddReturn = input(false);

  /**
   * Event emitter for selected date or date range
   * Array of (one or two) YYYY-MM-DD strings
   */
  selectedDatesChange = output<[string] | [string, string]>();

  /**
   * Event emitter for close event
   */
  // eslint-disable-next-line @angular-eslint/no-output-native
  close = output<void>();

  /**
   * Event emitter for monthChange
   */
  monthChange = output<Month>();

  visibleRangeChange = output<DateRange>();

  addReturn = output<void>();

  readonly ButtonTheme = ButtonTheme;
  readonly IconPosition = IconPosition;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly CalendarNavigationType = CalendarNavigationType;

  titleAreaConfig = computed(() => ({ ...DEFAULT_TITLE_AREA_CONFIGURATION, ...this.titleAreaConfiguration() }));
  startDate: ModelSignal<string> = model<string>('');
  endDate: ModelSignal<string> = model<string>('');
  startDateError: WritableSignal<string | undefined> = signal(undefined);
  endDateError: WritableSignal<string | undefined> = signal(undefined);
  dateSelectedLabel = computed(() => {
    if (this.isDateRange() && this.startDate() && this.endDate()) {
      return this.uiLabels()?.selectedDates;
    } else if (!this.isDateRange() && this.startDate()) {
      return this.uiLabels()?.selectedDate;
    }
    return undefined;
  });
  NotificationTheme = NotificationTheme;
  NotificationLayout = NotificationLayout;

  minDate = computed(
    () => {
      const selectableDatesRange = this.selectableDatesRange();
      const disabledDateRanges = this.disabledDateRanges();
      if (selectableDatesRange !== undefined || disabledDateRanges !== undefined) {
        return selectableDatesRange?.[0] ?? this.currentDate;
      }
      return undefined;
    },
    { equal: LocalDate.equals }
  );
  maxDate = computed(
    () => {
      const selectableDatesRange = this.selectableDatesRange();
      const disabledDateRanges = this.disabledDateRanges();
      if (selectableDatesRange !== undefined || disabledDateRanges !== undefined) {
        return selectableDatesRange?.[1] ?? this.defaultMaxDate;
      }
      return undefined;
    },
    { equal: LocalDate.equals }
  );
  selectedDateSelection: WritableSignal<DateSelection> = signal({ startDate: undefined, endDate: undefined });
  dateFormat = computed(() => new DateFormat(this.dateLabels()));
  isMobile = this.mediaQueryService.isBreakpoint('mobile');
  private readonly currentDate: LocalDate = LocalDate.now();
  private readonly defaultMaxDate = this.currentDate.plusDays(DAYS_AMOUNT).firstDayOfMonth.plusMonths(1).minusDays(1);
  private readonly dateFormatPattern = new Pattern('dateFormatUserInput', true);
  private readonly isLaptopUp = this.mediaQueryService.isBreakpoint('laptop_up');

  constructor() {
    effect(() => {
      const selectedDates = this.selectedDates();
      if (selectedDates !== undefined) {
        this.selectedDateSelection.set(this.dateArrayToDateSelection(selectedDates));
      }
    });
  }

  ngAfterViewInit(): void {
    const titleElement = this.titleElement()?.nativeElement;

    if (this.focusHeaderOnInit() && titleElement) {
      titleElement.focus();
    }
  }

  onDatesSelected(dates: DateSelection): void {
    if (dates.startDate) {
      this.startDate.set(this.dateFormat().format(dates.startDate, this.dateFormatPattern));
    } else {
      this.startDate.set(undefined);
    }

    if (dates.endDate) {
      this.endDate.set(this.dateFormat().format(dates.endDate, this.dateFormatPattern));
    } else {
      this.endDate.set(undefined);
    }

    if (this.isDateRange()) {
      this.selectedDatesChange.emit([
        dates.startDate ? dates.startDate.toString() : undefined,
        dates.endDate ? dates.endDate.toString() : undefined,
      ]);
    } else {
      this.selectedDatesChange.emit([dates.startDate ? dates.startDate.toString() : undefined]);
    }
    this.clearErrors();
  }

  setDate(event: Event): void {
    const element = event.target as HTMLInputElement;
    const isEndDate = element.name === 'endDate';
    let errorMessage;

    const userInputISOString = new ParseUserInput(element.value, this.dateLabels().dateFormatUserInput).getISOString();
    if (!userInputISOString && element.value !== '') {
      errorMessage = this.uiLabels().error.invalidDate;
    } else if (userInputISOString) {
      try {
        const date = LocalDate.fromTzDate(new TzDate(userInputISOString));
        if (
          !date.isBetween(this.minDate(), this.maxDate()) ||
          (this.disabledDateRanges() && this.disabledDateRanges().some((range) => date.isBetween(range[0], range[1])))
        ) {
          errorMessage = this.uiLabels().error.outOfRange;
        } else {
          this.verifyDateSelection(date, isEndDate);
          errorMessage = undefined;
        }
      } catch (e) {
        switch (e.message) {
          case 'startAfterEnd':
            errorMessage = this.uiLabels().error.startAfterEnd;
            break;
          case 'endBeforeStart':
            errorMessage = this.uiLabels().error.endBeforeStart;
            break;
          case 'outOfRange':
            errorMessage = this.uiLabels().error.outOfRange;
            break;
          default:
            errorMessage = this.uiLabels().error.invalidDate;
            break;
        }
      }
    } else if (element.value === '') {
      this.verifyDateSelection(undefined, isEndDate);
      errorMessage = undefined;
    }

    if (isEndDate) {
      this.endDateError.set(errorMessage);
    } else {
      this.startDateError.set(errorMessage);
    }
  }

  clearErrors(): void {
    this.endDateError.set(undefined);
    this.startDateError.set(undefined);
  }

  clearDates(): void {
    // we act to clear dates only if any of start date or end date are previously set
    if (isPresent(this.selectedDateSelection().startDate) || isPresent(this.selectedDateSelection().endDate)) {
      this.selectedDateSelection.set({ startDate: undefined, endDate: undefined });
      this.onDatesSelected({ startDate: undefined, endDate: undefined });
      if (this.isLaptopUp() && !this.embedded()) {
        this.scrollToTop();
      }
    }
  }

  closeDatepicker(): void {
    this.close.emit();
  }

  private verifyDateSelection(date: LocalDate | undefined, isEndDate: boolean): void {
    const currentSelection: DateSelection = this.selectedDateSelection();
    const currentCorrespondingDateSelection = isEndDate ? currentSelection.endDate : currentSelection.startDate;
    const isAlreadySelected = currentCorrespondingDateSelection && date?.equals(currentCorrespondingDateSelection);
    const isEndBeforeStart = isEndDate && currentSelection.startDate && date?.lt(this.minDate());
    const isOutOfRange = date?.lt(this.minDate()) || date?.gt(this.maxDate());

    if (isAlreadySelected) {
      return;
    } else if (isEndBeforeStart) {
      throw new Error('endBeforeStart');
    } else if (isOutOfRange) {
      throw new Error('outOfRange');
    } else {
      const newSelections = isEndDate ? { endDate: date } : { startDate: date };
      this.selectedDateSelection.set({ ...currentSelection, ...newSelections });
    }
  }

  private scrollToTop() {
    requestAnimationFrame(() => {
      this.hostElement.nativeElement.scrollIntoView();
    });
  }

  private dateArrayToDateSelection(dateArray: LocalDate[]): DateSelection {
    const dateSelection: DateSelection = { startDate: undefined, endDate: undefined };
    if (dateArray[0] && this.isWithinSelectableRange(dateArray[0])) {
      dateSelection.startDate = dateArray[0];
      if (dateArray[1] && this.isWithinSelectableRange(dateArray[1])) {
        dateSelection.endDate = dateArray[1];
      }
    }
    return dateSelection;
  }

  private isWithinSelectableRange(date: LocalDate): boolean {
    return this.minDate().lte(date) && this.maxDate().gte(date);
  }
}
